import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import Navbar from './Navbar.jsx';
import Footer from './Footer.jsx';
import OverlayMenu from './OverlayMenu.jsx';
import AnnouncementBar from './AnnouncementBar.jsx';
import MainMenu from './MainMenu.jsx';

import { client } from '../lib/client';
import { announcementQuery } from '../lib/queries';

const OVERLAY_TRANSITION_MS = 200; // matches your overlay duration-200

function Layout({ children }) {
  const location = useLocation();
  const isProductDetailsPage = location.pathname.includes('/product');
  const isPressPage = location.pathname.startsWith('/pages/press');

  // Overlay state (lifted)
  const [menuOpen, setMenuOpen] = useState(false);

  // Delay showing the bar until overlay fully closes (prevents jump)
  const [suppressBar, setSuppressBar] = useState(false);
  useEffect(() => {
    if (menuOpen) {
      setSuppressBar(true);
    } else {
      const t = setTimeout(() => setSuppressBar(false), OVERLAY_TRANSITION_MS + 20);
      return () => clearTimeout(t);
    }
  }, [menuOpen]);

  // Measure real navbar height so padding is exact
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);
  useLayoutEffect(() => {
    const update = () => setNavHeight(navRef.current?.offsetHeight || 0);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Announcement data
  const [announcement, setAnnouncement] = useState(null);
  useEffect(() => {
    let mounted = true;
    client.fetch(announcementQuery).then((res) => {
      if (!mounted) return;
      const now = new Date();
      const withinWindow =
        (!res?.startDate || new Date(res.startDate) <= now) &&
        (!res?.endDate || now <= new Date(res.endDate));
      setAnnouncement({
        enabled: !!res?.enabled && withinWindow && !!res?.message,
        message: res?.message || '',
        height: res?.height ?? 36,
        speedMs: res?.speedMs ?? 16000,
      });
    });
    return () => { mounted = false; };
  }, []);

  const barHeight = announcement?.height ?? 36;
  const barVisible = !!announcement?.enabled && !suppressBar;

  return (
    <>
      {/* Top announcement bar (hidden while overlay is opening/closing) */}
      <AnnouncementBar
        enabled={barVisible}
        message={announcement?.message}
        height={barHeight}
        speedMs={announcement?.speedMs}
        style={{ backgroundColor: '#fff', color: 'var(--customNavy)' }}
      />

      {/* Navbar (offset only when bar is visible) */}
      <Navbar
        navRef={navRef}
        offsetTop={barVisible ? barHeight : 0}
        onToggleMenu={() => setMenuOpen((v) => !v)}
        menuOpen={menuOpen}
      />

       {/* Mobile-only Search + Shop menu strip */}
       <MainMenu />

      {/* Full-screen overlay: keep it above nav + bar */}
      <OverlayMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
      />

      {/* Page content padding = (bar if visible) + real nav height */}
      <div
        className={`${isProductDetailsPage ? 'pt-4' : 'py-6'} ${
          isPressPage ? '' : 'min-h-screen'
        } flex flex-col`}
        style={{
          paddingTop: (barVisible ? barHeight : 0) + navHeight,
        }}
      >
        {children}
      </div>

      <Footer />
    </>
  );
}

export default Layout;
